import { cn, EmptyState, Skeleton } from '@sjuni/ui';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.6 — the table half of every list view.
 *
 * "Every list has: search, relevant filters, column sorting, per-page selector,
 * bulk selection with bulk actions, a 'show deleted' toggle with restore,
 * sticky header, keyboard row navigation, and skeleton loading."
 *
 * The controls around it live in `ResourceToolbar`; this is the grid. It is a
 * real `<table>` rather than a grid of divs, because the thing an editor does
 * with a list is compare one row against another down a column, and that is
 * what a table is for — and what a screen reader announces it as.
 *
 * Keyboard row navigation is the part usually skipped. Arrow keys move between
 * rows, Enter opens one; without it the only way through a 25-row list is 25
 * tab stops per row.
 */
export interface Column<TRow> {
  key: string;
  label: string;
  /** Whether the server will sort on it — the allow-list is `sortable()`. */
  sortable?: boolean;
  /** Right-aligned for dates and counts, which read better that way. */
  align?: 'start' | 'end';
  render: (row: TRow) => React.ReactNode;
}

export interface ResourceTableProps<TRow> {
  rows: TRow[];
  columns: Column<TRow>[];
  rowKey: (row: TRow) => number | string;
  loading?: boolean;
  /** `-column` for descending, matching the API's `?sort=`. */
  sort?: string;
  onSortChange?: (sort: string) => void;
  onOpen?: (row: TRow) => void;
  /** §4.6's bulk selection. Omit to render no checkbox column. */
  selected?: Set<number | string>;
  onSelectedChange?: (next: Set<number | string>) => void;
  emptyTitle: string;
  emptyBody?: string;
  /** The primary action for an empty list — §6.4. */
  emptyAction?: React.ReactNode;
}

export function ResourceTable<TRow>({
  rows,
  columns,
  rowKey,
  loading = false,
  sort,
  onSortChange,
  onOpen,
  selected,
  onSelectedChange,
  emptyTitle,
  emptyBody,
  emptyAction,
}: ResourceTableProps<TRow>) {
  const { t } = useTranslation();

  const selectable = selected !== undefined && onSelectedChange !== undefined;
  const allSelected =
    selectable && rows.length > 0 && rows.every((row) => selected.has(rowKey(row)));

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t('common.loading')}</span>
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-12 w-full rounded-card" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    // §6.4 — "Empty states explain what the screen is for and offer the
    // primary action."
    return (
      <EmptyState
        className="py-16 text-center"
        title={emptyTitle}
        description={emptyBody}
        action={emptyAction}
      />
    );
  }

  return (
    /*
     * The table scrolls inside this box, not with the page.
     *
     * `position: sticky` resolves against the nearest *scrolling* ancestor,
     * and `overflow-x-auto` here makes this div one — so a header stuck to
     * `top-16` was pushed 64px down inside the box and painted over the first
     * row, which read as the list quietly dropping a record. Giving the box a
     * height makes it the scroller it already claimed to be, and the header
     * sticks to its top where §4.6 wants it.
     */
    <div className="max-h-[70vh] overflow-auto rounded-card border border-subtle">
      {/*
        `border-separate` with no spacing, not `border-collapse`. §4.6 asks
        for a sticky header, and a collapsed table cannot have one: the
        borders belong to the table rather than the cells, so the sticky
        `thead` is lifted out of flow and painted *over* the first row — which
        looked exactly like the list dropping a record. The cells carry the
        rules instead.
      */}
      <table className="w-full border-separate border-spacing-0 text-body-sm">
        <thead className="sticky top-0 z-10">
          <tr>
            {selectable ? (
              <th scope="col" className="w-10 border-b border-subtle bg-surface-alt px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  aria-label={t('list.selectAll')}
                  onChange={(event) =>
                    onSelectedChange(
                      event.target.checked ? new Set(rows.map(rowKey)) : new Set<number | string>(),
                    )
                  }
                  className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                />
              </th>
            ) : null}

            {columns.map((column) => (
              <SortableHeader
                key={column.key}
                column={column}
                sort={sort}
                onSortChange={onSortChange}
              />
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const key = rowKey(row);

            return (
              <tr
                key={key}
                /*
                 * One tab stop for the whole list, then arrow keys within it.
                 * The alternative — every row focusable — puts the pagination
                 * twenty-five tab presses away.
                 */
                tabIndex={index === 0 ? 0 : -1}
                data-row-index={index}
                onKeyDown={(event) => onRowKeyDown(event, index, rows.length, () => onOpen?.(row))}
                onDoubleClick={() => onOpen?.(row)}
                className={cn(
                  'transition-colors duration-fast [&>td]:border-t [&>td]:border-subtle',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-navy-600',
                  onOpen && 'cursor-pointer hover:bg-surface-alt',
                  selectable && selected.has(key) && 'bg-accent-tint-navy',
                )}
              >
                {selectable ? (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(key)}
                      aria-label={t('list.selectRow')}
                      onChange={(event) => {
                        const next = new Set(selected);

                        if (event.target.checked) {
                          next.add(key);
                        } else {
                          next.delete(key);
                        }

                        onSelectedChange(next);
                      }}
                      className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                    />
                  </td>
                ) : null}

                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn('px-3 py-3 align-middle', column.align === 'end' && 'text-end')}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader<TRow>({
  column,
  sort,
  onSortChange,
}: {
  column: Column<TRow>;
  sort?: string;
  onSortChange?: (sort: string) => void;
}) {
  const active = sort === column.key || sort === `-${column.key}`;
  const descending = sort === `-${column.key}`;

  if (!column.sortable || !onSortChange) {
    return (
      <th
        scope="col"
        className={cn(
          'border-b border-subtle bg-surface-alt px-3 py-3 text-caption font-extrabold tracking-wide text-muted',
          column.align === 'end' ? 'text-end' : 'text-start',
        )}
      >
        {column.label}
      </th>
    );
  }

  return (
    <th
      scope="col"
      /*
       * `aria-sort` on the header rather than an icon alone: the arrow says
       * which way to a sighted reader and nothing at all to anyone else.
       */
      aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
      className={cn(
        'border-b border-subtle bg-surface-alt px-3 py-3',
        column.align === 'end' ? 'text-end' : 'text-start',
      )}
    >
      <button
        type="button"
        // First click sorts ascending; clicking the active column reverses it.
        onClick={() => onSortChange(active && !descending ? `-${column.key}` : column.key)}
        className="inline-flex items-center gap-1 text-caption font-extrabold tracking-wide text-muted hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
      >
        {column.label}
        {active ? (
          descending ? (
            <ArrowDown size={12} strokeWidth={2} aria-hidden="true" />
          ) : (
            <ArrowUp size={12} strokeWidth={2} aria-hidden="true" />
          )
        ) : null}
      </button>
    </th>
  );
}

/** §4.6's keyboard row navigation: up/down move, Enter opens. */
function onRowKeyDown(
  event: React.KeyboardEvent<HTMLTableRowElement>,
  index: number,
  total: number,
  open: () => void,
): void {
  if (event.key === 'Enter') {
    event.preventDefault();
    open();

    return;
  }

  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
    return;
  }

  event.preventDefault();

  // Clamped rather than wrapping: a list is a column of rows with a top and a
  // bottom, and jumping from the last to the first loses your place.
  const next = Math.min(Math.max(index + (event.key === 'ArrowDown' ? 1 : -1), 0), total - 1);

  const target = event.currentTarget.parentElement?.querySelector<HTMLElement>(
    `[data-row-index="${next}"]`,
  );

  if (target) {
    // Roving tabindex: only the focused row is in the tab order.
    event.currentTarget.tabIndex = -1;
    target.tabIndex = 0;
    target.focus();
  }
}
